/**
 * Button
 * ======
 * The one button vocabulary used site-wide — variant handles look,
 * `to`/`href` handle whether it renders as a router Link, external
 * anchor, or plain button. Hover is colour/glow only — the button never
 * moves, scales or shifts position, on hover, focus or active.
 */

import { forwardRef, useRef } from 'react';
import { Link } from 'react-router-dom';
import { prefetchRoute } from '../../pages/routes';

const VARIANT_CLASSES = {
  primary: 'bg-amber-500 text-ink hover:bg-amber-bright hover:shadow-[0_8px_28px_-6px_rgba(232,121,45,0.55)] px-7 py-3.5',
  outline: 'bg-transparent text-cream border border-cream-dim/40 hover:border-amber-bright hover:text-amber-bright hover:bg-amber-500/5 hover:shadow-soft px-7 py-3.5',
  ghost: 'bg-white/10 backdrop-blur-sm text-cream border border-white/15 hover:bg-white/20 hover:border-white/25 hover:shadow-soft px-6 py-3',
  invert: 'bg-ink text-cream hover:bg-ink/85 hover:shadow-[0_8px_28px_-6px_rgba(0,0,0,0.5)] px-7 py-3.5',
  'invert-outline': 'bg-transparent text-ink border border-ink/40 hover:border-ink hover:bg-ink/10 hover:shadow-soft px-7 py-3.5',
  text: 'bg-transparent text-amber-bright p-0 link-underline'
};

// The button itself never moves on hover/active (see file header) - but a
// couple of px of nudge on just this icon glyph is scoped to a decorative
// child, not the button's own box, so it doesn't reintroduce that.
const Arrow = () => (
  <svg
    width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true"
    className="transition-transform duration-300 ease-out group-hover:translate-x-0.5 group-active:translate-x-1"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

const Button = forwardRef(({
  to,
  href,
  onClick,
  type = 'button',
  variant = 'primary',
  arrow = false,
  className = '',
  children,
  ...props
}, ref) => {
  const lastClickRef = useRef(0);
  const caseClasses = variant === 'text' ? 'text-sm' : 'uppercase tracking-wider text-xs';

  const classes = `group inline-flex items-center justify-center gap-2 font-sans font-semibold transition-[background-color,border-color,color,box-shadow,filter] duration-300 ease-out active:brightness-90 active:duration-100 ${caseClasses} ${
    variant === 'text' ? '' : 'rounded-[2px]'
  } ${VARIANT_CLASSES[variant]} ${className}`;

  const inner = (
    <span className={classes}>
      {children}
      {arrow && <Arrow />}
    </span>
  );

  if (to) {
    // Warms the target route's code-split chunk on hover/focus, ahead of
    // an actual click - see src/pages/routes.js.
    const { onMouseEnter, onFocus, ...restProps } = props;
    const handleHover = (e) => { prefetchRoute(to); if (onMouseEnter) onMouseEnter(e); };
    const handleFocus = (e) => { prefetchRoute(to); if (onFocus) onFocus(e); };
    return (
      <Link ref={ref} to={to} className="inline-block" onMouseEnter={handleHover} onFocus={handleFocus} {...restProps}>
        {inner}
      </Link>
    );
  }

  if (href) {
    // Guards against a rapid double-click opening two tabs for the same
    // external link (Toast reservations/ordering, delivery platforms).
    const { onClick: externalOnClick, ...restProps } = props;
    const handleExternalClick = (e) => {
      const now = Date.now();
      if (now - lastClickRef.current < 800) { e.preventDefault(); return; }
      lastClickRef.current = now;
      if (externalOnClick) externalOnClick(e);
    };
    return (
      <a ref={ref} href={href} target="_blank" rel="noopener noreferrer" className="inline-block" onClick={handleExternalClick} {...restProps}>
        {inner}
      </a>
    );
  }

  return (
    <button ref={ref} type={type} onClick={onClick} className="inline-block" {...props}>
      {inner}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
